import React, { useState } from 'react';
import {
  Box,
  Typography,
  Grid,
  FormControl,
  MenuItem,
  TextField,
  styled,
} from '@mui/material';
import { PatternFormat } from 'react-number-format';
import { ipRoles, tooRoles, ContactRole, ThirdPartyContact } from '@/constans/contactRoles';
import { validateKazakhPhone } from '@/utils/phoneValidation';
import { validateCyrillic, filterCyrillicInput } from '@/utils/cyrillicValidation';

// Стилизованные компоненты (используем те же стили что и в основной форме)
const StyledTextField = styled(TextField)(
  ({ theme }) => `
    & .MuiInput-underline:after {
      border-bottom-color: white;
      background: white;
      outline:none;
    }
    & .MuiFilledInput-root {
      background: #F3F3F7;
      border-radius:12px;
      outline:none;
      
      &:before {
        border: none;
        outline:none;
        &:hover {
          border: none;
        }
      };
      &:after {
        border:none;
        outline:none;
      }
     
    }
   & .MuiFilledInput-input:-webkit-autofill {
    border-radius:12px;
   }
  
   & .MuiFilledInput-root:hover:not(.Mui-disabled, .Mui-error):before {
    border-bottom: none;
      }
     
  `
);

const StyledSelect = styled(TextField)(
  ({ theme }) => ` 
    &.MuiFilledInput-root {
      background: #F3F3F7;
      border-radius:12px;
      outline:none;
      
      &:before {
        border: none;
        outline:none;
        &:hover {
          border: none;
        }
      };
      &:after {
        border:none;
        outline:none;
      }
     
    }
    
    &.MuiFilledInput-input:-webkit-autofill {
      border-radius:12px;
     }
    
     &.MuiFilledInput-root:hover:not(.Mui-disabled, .Mui-error):before {
      border-bottom: none;
        }   
        `
);

interface ThirdPartyContactsProps {
  businessType?: 'IP' | 'TOO'; // Тип бизнеса для определения доступных ролей
  contacts: ThirdPartyContact[];
  onContactsChange: (contacts: ThirdPartyContact[]) => void;
  errors?: {
    contact1?: { fio?: string; mobile?: string; role?: string };
    contact2?: { fio?: string; mobile?: string; role?: string };
  };
}

const ThirdPartyContacts: React.FC<ThirdPartyContactsProps> = ({
  businessType = 'IP',
  contacts,
  onContactsChange,
  errors = {},
}) => {
  const [phoneFocused, setPhoneFocused] = useState<{ [key: number]: boolean }>({});
  
  // Выбираем роли в зависимости от типа бизнеса
  const availableRoles = businessType === 'IP' ? ipRoles : tooRoles;

  // Инициализируем контакты если их нет
  const initializeContacts = () => {
    const initialContacts: ThirdPartyContact[] = [
      { fio: '', mobile: '', role: { code: '', value: '' } },
      { fio: '', mobile: '', role: { code: '', value: '' } },
    ];
    
    // Копируем существующие контакты
    contacts.forEach((contact, index) => {
      if (index < 2) {
        initialContacts[index] = { ...contact };
      }
    });
    
    return initialContacts;
  };

  const currentContacts = contacts.length >= 2 ? contacts : initializeContacts();

  const handleContactChange = (index: number, field: keyof ThirdPartyContact, value: any) => {
    const updatedContacts = [...currentContacts];
    
    if (field === 'role') {
      updatedContacts[index] = {
        ...updatedContacts[index],
        role: value,
      };
    } else if (field === 'fio') {
      // Фильтруем ввод для ФИО - оставляем только кириллицу
      const filteredValue = filterCyrillicInput(value);
      updatedContacts[index] = {
        ...updatedContacts[index],
        [field]: filteredValue,
      };
    } else {
      updatedContacts[index] = {
        ...updatedContacts[index],
        [field]: value,
      };
    }
    
    onContactsChange(updatedContacts);
  };

  const handlePhoneChange = (index: number, value: string) => {
    handleContactChange(index, 'mobile', value);
  };

  const handleRoleChange = (index: number, roleCode: string) => {
    const selectedRole = availableRoles.find(role => role.code === roleCode);
    if (selectedRole) {
      const updatedContacts = [...currentContacts];
      updatedContacts[index] = {
        ...updatedContacts[index],
        role: {
          code: selectedRole.code,
          // Если выбрано "другое", сохраняем текущий кастомный текст или пустую строку
          value: roleCode === 'other' ? updatedContacts[index].role.value : selectedRole.value,
        },
      };
      onContactsChange(updatedContacts);
    }
  };

  const handleCustomRoleChange = (index: number, customRoleValue: string) => {
    const updatedContacts = [...currentContacts];
    updatedContacts[index] = {
      ...updatedContacts[index],
      role: {
        ...updatedContacts[index].role,
        value: customRoleValue,
      },
    };
    onContactsChange(updatedContacts);
  };

  const renderContactForm = (contactIndex: number, title: string) => {
    const contact = currentContacts[contactIndex];
    const contactErrors = contactIndex === 0 ? errors.contact1 : errors.contact2;

    return (
      <Box key={contactIndex}>
        {/* Заголовок контакта */}
        <Typography
          sx={{
            fontFamily: 'Suisse Intl',
            fontWeight: 600,
            fontSize: '16px',
            lineHeight: '1.5em',
            color: '#1D1D1E',
            marginBottom: '12px',
            marginTop: contactIndex > 0 ? '16px' : '0',
          }}
        >
          {title}
        </Typography>

        {/* Форма контакта */}
        <Grid
          container
          spacing={2}
          sx={(theme) => ({
            display: 'grid',
            gridTemplateColumns: contact?.role?.code === 'other' ? '1fr 1fr 1fr 1fr' : '1fr 1fr 1fr',
            gap: '16px',
            [theme.breakpoints.down('md')]: { 
              gridTemplateColumns: '1fr',
              gap: '12px',
            },
          })}
        >
          {/* ФИО */}
          <Grid item sx={{ width: '100%' }}>
            <StyledTextField
              id={`contact${contactIndex + 1}Fio`}
              label="ФИО"
              variant="filled"
              fullWidth
              value={contact?.fio || ''}
              onChange={(e) => handleContactChange(contactIndex, 'fio', e.target.value)}
              error={!!contactErrors?.fio}
              sx={(theme) => ({
                borderRadius: '12px',
                border: contactErrors?.fio ? '1px solid red' : 'none',
                '& fieldset': { border: 'none' },
                [theme.breakpoints.down('sm')]: { width: '100%' },
              })}
            />
            {contactErrors?.fio && (
              <Typography
                sx={{
                  color: '#d32f2f',
                  fontSize: '12px',
                  marginTop: '4px',
                  marginLeft: '16px',
                }}
              >
                {contactErrors.fio}
              </Typography>
            )}
          </Grid>

          {/* Мобильный номер */}
          <Grid item sx={{ width: '100%' }}>
            <PatternFormat
              customInput={StyledTextField}
              allowEmptyFormatting={phoneFocused[contactIndex]}
              format="+7 (###) ###-##-##"
              mask="_"
              id={`contact${contactIndex + 1}Phone`}
              label="Мобильный номер"
              variant="filled"
              type="tel"
              value={contact?.mobile || ''}
              onValueChange={({ value }) => {
                handlePhoneChange(contactIndex, value);
              }}
              onFocus={() => setPhoneFocused(prev => ({ ...prev, [contactIndex]: true }))}
              onBlur={() => setPhoneFocused(prev => ({ ...prev, [contactIndex]: false }))}
              error={!!contactErrors?.mobile}
              sx={(theme) => ({
                borderRadius: '12px',
                width: '100%',
                border: contactErrors?.mobile ? '1px solid red' : 'none',
                [theme.breakpoints.down('sm')]: { width: '100%' },
              })}
            />
            {contactErrors?.mobile && (
              <Typography
                sx={{
                  color: '#d32f2f',
                  fontSize: '12px',
                  marginTop: '4px',
                  marginLeft: '16px',
                }}
              >
                {contactErrors.mobile}
              </Typography>
            )}
          </Grid>

          {/* Роль */}
          <Grid item sx={{ width: '100%' }}>
            <FormControl
              sx={(theme) => ({
                width: '100%',
                borderRadius: '12px',
                border: contactErrors?.role ? '1px solid red' : 'none',
              })}
            >
              <StyledSelect
                select
                label="Роль"
                variant="filled"
                fullWidth
                value={contact?.role?.code || ''}
                onChange={(e) => handleRoleChange(contactIndex, e.target.value)}
                error={!!contactErrors?.role}
                sx={{
                  '& .MuiFilledInput-root': {
                    background: '#F3F3F7',
                    borderRadius: '12px',
                    '&:before, &:after': { border: 'none' },
                    '&:hover:before': { border: 'none' },
                  },
                }}
              >
                {availableRoles.map((role) => (
                  <MenuItem key={role.code} value={role.code}>
                    {role.value || 'Укажите роль'}
                  </MenuItem>
                ))}
              </StyledSelect>
            </FormControl>
            {contactErrors?.role && (
              <Typography
                sx={{
                  color: '#d32f2f',
                  fontSize: '12px',
                  marginTop: '4px',
                  marginLeft: '16px',
                }}
              >
                {contactErrors.role}
              </Typography>
            )}
          </Grid>

          {/* Кастомная роль (показывается только когда выбрано "другое") */}
          {contact?.role?.code === 'other' && (
            <Grid item sx={{ width: '100%' }}>
              <StyledTextField
                id={`contact${contactIndex + 1}CustomRole`}
                label="Укажите роль"
                variant="filled"
                fullWidth
                value={contact?.role?.value || ''}
                onChange={(e) => handleCustomRoleChange(contactIndex, e.target.value)}
                error={!!contactErrors?.role}
                sx={(theme) => ({
                  borderRadius: '12px',
                  border: contactErrors?.role ? '1px solid red' : 'none',
                  '& fieldset': { border: 'none' },
                  [theme.breakpoints.down('sm')]: { width: '100%' },
                })}
              />
              {contactErrors?.role && contact?.role?.code === 'other' && (
                <Typography
                  sx={{
                    color: '#d32f2f',
                    fontSize: '12px',
                    marginTop: '4px',
                    marginLeft: '16px',
                  }}
                >
                  {contactErrors.role}
                </Typography>
              )}
            </Grid>
          )}
        </Grid>
      </Box>
    );
  };

  return (
    <Box sx={{ width: '100%', marginTop: '24px' }}>
      {/* Основной заголовок */}
      <Typography
        sx={{
          fontFamily: 'Roboto Flex',
          fontWeight: 700,
          fontSize: '20px',
          lineHeight: '1.2em',
          color: '#1D1D1E',
          marginBottom: '12px',
        }}
      >
        Контакты третьих лиц
      </Typography>
      
      {/* Описание */}
      <Typography
        sx={{
          fontFamily: 'Suisse Intl',
          fontWeight: 400,
          fontSize: '16px',
          lineHeight: '1.5em',
          color: '#565658',
          marginBottom: '24px',
        }}
      >
        Добавьте контакты третьих лиц, это повысит вероятность получения займа
      </Typography>

      {/* Формы контактов */}
      {renderContactForm(0, 'Контактное лицо 1')}
      {renderContactForm(1, 'Контактное лицо 2')}
    </Box>
  );
};

export default ThirdPartyContacts;
